/**
 * Native full-screen for one element.
 *
 * WHY THE PLATFORM API rather than a CSS `position: fixed; inset: 0` trick. The Fullscreen
 * API takes the element out of the page's stacking context entirely, so the app's own header
 * and sidebar cannot paint over it and no `z-index` arms race is needed. It also gives the
 * operator the escape they already expect — the Escape key — for free, and reports the state
 * back so a toggle can be honest about whether it worked.
 *
 * `isFullscreen` is derived from the `fullscreenchange` EVENT, never from the click. The user
 * can leave full-screen by pressing Escape, by a gesture, or because the browser refused, and
 * a button tracking its own optimistic state would then be inverted — showing "Exit" on a
 * window that is not full-screen.
 *
 * Safari still ships only the `webkit`-prefixed API, so both are probed. A browser with
 * neither reports `supported: false` and a caller can hide the control rather than offering a
 * button that does nothing.
 */
import { useCallback, useEffect, useState, type RefObject } from 'react';

interface FullscreenDoc extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}
interface FullscreenEl extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

function current(): Element | null {
  const d = document as FullscreenDoc;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

export interface Fullscreen {
  isFullscreen: boolean;
  supported: boolean;
  toggle: () => void;
  /** Set when a request was refused, so the UI can say so instead of doing nothing. */
  error: string | null;
}

export function useFullscreen(ref: RefObject<HTMLElement | null>): Fullscreen {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supported =
    typeof document !== 'undefined' &&
    (document.fullscreenEnabled === true ||
      typeof (document.documentElement as FullscreenEl).webkitRequestFullscreen === 'function');

  useEffect(() => {
    const sync = (): void => setIsFullscreen(current() === ref.current && ref.current !== null);
    // Both event names: the prefixed one is the only one Safari fires.
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [ref]);

  const toggle = useCallback(() => {
    const el = ref.current as FullscreenEl | null;
    if (!el) return;
    setError(null);

    if (current() !== null) {
      const d = document as FullscreenDoc;
      void Promise.resolve(d.exitFullscreen?.() ?? d.webkitExitFullscreen?.()).catch(() => {
        /* Leaving full-screen failing is not worth a message: the operator can press Escape. */
      });
      return;
    }

    /*
     * A refusal is reported, not swallowed. `requestFullscreen` rejects when the call is not
     * user-activated or when an iframe lacks `allow="fullscreen"` — both produce a button
     * that appears broken, and an operator deserves to know it was the browser's decision.
     */
    void Promise.resolve(el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.()).catch(
      (e: unknown) => {
        setError(
          e instanceof Error
            ? `Full screen was refused: ${e.message}`
            : 'Full screen was refused by the browser.',
        );
      },
    );
  }, [ref]);

  return { isFullscreen, supported, toggle, error };
}

/**
 * Full screen for the WHOLE PRODUCT, not one panel.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE DOCUMENT AND NOT A WRAPPER DIV
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Requesting it on `document.documentElement` is the one target that cannot go wrong. Any
 * element inside the app is subject to the layout it sits in — a wrapper with `overflow:
 * hidden`, a flex parent that will not let it fill, a stacking context that clips it — and
 * the failure mode is a full-screen window showing a correctly-sized app in one corner. The
 * root element has no parent to fight.
 *
 * It also means every screen in the product gets this for free, including ones that do not
 * exist yet, and it composes correctly with the twin's own stage full-screen: those are
 * different elements, so `isFullscreen` is false for this hook while the stage is expanded
 * and the header's button honestly reports the window's state rather than the stage's.
 *
 * WHAT THIS DOES NOT DO is hide the app's own chrome. Full screen removes the BROWSER's
 * furniture — tabs, address bar, the OS window frame — which is what a control-room wall
 * display needs and what an operator means by the word. Hiding the header and the rail as
 * well would be a kiosk mode, which is a different feature and would leave no way back.
 */
export function useDocumentFullscreen(): Fullscreen {
  /*
   * A ref built once, deliberately not `useRef(document.documentElement)`: that reads the
   * DOM during render, which is wrong under SSR and under React's strict double-render. The
   * lazy initialiser runs on the client, once.
   */
  const [ref] = useState<RefObject<HTMLElement | null>>(() => ({
    current: typeof document === 'undefined' ? null : document.documentElement,
  }));
  return useFullscreen(ref);
}
